import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { RootState } from '../../store';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import logApi from '../../services/api/log.api';
import applicationApi, { Application } from '../../services/api/application.api';
import MainLayout from '../../components/layout/MainLayout';
import ApplicationRequired from '../../components/common/ApplicationRequired';

interface VerificationStatus {
  checking: boolean;
  success: boolean;
  message: string;
  lastCheck?: Date;
  logsReceived?: number;
}

export default function LogAgentInstallPage() {
  const { t } = useTranslation('agents');
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const globalSelectedAppId = useAppSelector(selectCurrentApplication);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedMethod, setSelectedMethod] = useState<'docker' | 'systemd' | 'kubernetes'>('docker');
  const [apiKey, setApiKey] = useState<string>('');
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [existingApplications, setExistingApplications] = useState<Application[]>([]);
  const [loadingApps, setLoadingApps] = useState(true);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [verification, setVerification] = useState<VerificationStatus>({
    checking: false,
    success: false,
    message: t('install.verification.noData'),
  });

  const isAllAppsSelected = !globalSelectedAppId || globalSelectedAppId === 'all';

  useEffect(() => {
    loadExistingApplications();
  }, []);

  // Monitor global application changes and auto-select if a specific app is already selected
  useEffect(() => {
    if (isAllAppsSelected && selectedApplication) {
      toast.error(
        t('logs.errors.cannotUseAllApps'),
        {
          duration: 6000,
          icon: '⚠️',
        }
      );
    } else if (globalSelectedAppId && globalSelectedAppId !== 'all' && existingApplications.length > 0) {
      const newApp = existingApplications.find(app => app.id === globalSelectedAppId);
      if (newApp && (!selectedApplication || selectedApplication.id !== newApp.id)) {
        setSelectedApplication(newApp);
        setApiKey(newApp.api_key);
      }
    }
  }, [globalSelectedAppId, existingApplications]);

  const loadExistingApplications = async () => {
    try {
      setLoadingApps(true);
      const response = await applicationApi.getAll();
      if (response.success && response.data) {
        const activeApps = response.data.filter((app) => app.status === 'active');
        setExistingApplications(activeApps);
      }
    } catch (error) {
      console.error('Failed to load applications:', error);
    } finally {
      setLoadingApps(false);
    }
  };

  // Filter applications to show: if a specific app is selected globally, show only that app
  const appsToDisplay = !isAllAppsSelected && globalSelectedAppId
    ? existingApplications.filter(app => app.id === globalSelectedAppId)
    : existingApplications;

  const generateApiKey = async () => {
    setGeneratingKey(true);
    try {
      const timestamp = new Date().toISOString().split('T')[0];
      const response = await applicationApi.create({
        name: `${t('agents.logs.name')} - ${timestamp}`,
        description: t('agents.logs.description'),
        language: 'log-agent',
        environment: 'production',
      });

      if (response.success && response.data) {
        setApiKey(response.data.api_key);
        setSelectedApplication(response.data);
        setExistingApplications([...existingApplications, response.data]);
        setCurrentStep(2);
      } else {
        throw new Error(t('install.apiKey.generating'));
      }
    } catch (error: any) {
      console.error('Failed to generate API key:', error);
      toast.error(error.response?.data?.error || t('logs.errors.failedToGenerate'), {
        duration: 5000,
        icon: '❌',
      });
    } finally {
      setGeneratingKey(false);
    }
  };

  const selectExistingApplication = (app: Application) => {
    setSelectedApplication(app);
    setApiKey(app.api_key);
    setCurrentStep(2);
  };

  const verifyConnection = async () => {
    setVerification({ checking: true, success: false, message: t('install.verification.checking') });

    try {
      const [, response] = await Promise.all([
        new Promise(resolve => setTimeout(resolve, 2000)),
        (async () => {
          const endTime = Date.now();
          const startTime = endTime - 5 * 60 * 1000; // Last 5 minutes
          // Buscar logs apenas da aplicação selecionada
          const appId = selectedApplication?.id || 'any';
          return await logApi.getAll(appId, { startTime, endTime, limit: 10 });
        })()
      ]);

      console.log('Log verification response:', response);

      if (response.success && response.data && response.data.length > 0) {
        setVerification({
          checking: false,
          success: true,
          message: t('logs.verification.success', { count: response.data.length }),
          lastCheck: new Date(),
          logsReceived: response.data.length,
        });
        setCurrentStep(4);
      } else {
        setVerification({
          checking: false,
          success: false,
          message: t('apm.verification.noTraces'),
          lastCheck: new Date(),
        });
      }
    } catch (error: any) {
      console.error('Log verification error:', error);
      setVerification({
        checking: false,
        success: false,
        message: `${t('install.verification.checking')}: ${error.message || t('install.verification.noData')}`,
        lastCheck: new Date(),
      });
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 3000);
    }).catch((err) => {
      console.error('Failed to copy:', err);
      toast.error(t('logs.errors.failedToCopy'), {
        duration: 3000,
        icon: '❌',
      });
    });
  };

  const getInstallCommand = () => {
    const baseUrl = 'https://api-monitoring.devskin.com';

    switch (selectedMethod) {
      case 'docker':
        return `# Prerequisites: Docker only!
docker --version

# Pull and run the agent (simple one-liner!)
docker run -d \\
  --name devskin-log-agent \\
  --restart unless-stopped \\
  -e DEVSKIN_API_KEY="${apiKey}" \\
  -e DEVSKIN_APPLICATION_ID="${selectedApplication?.id}" \\
  -e DEVSKIN_SERVER_URL="${baseUrl}" \\
  -e LOG_SOURCE_PATH="/var/log/**/*.log" \\
  -e LOG_SOURCE_FORMAT="auto" \\
  -v /var/log:/var/log:ro \\
  devskin/log-agent:latest

# ⚠️  IMPORTANT: Replace LOG_SOURCE_PATH with your actual log location!
# Examples:
# Single file:         -e LOG_SOURCE_PATH="/var/log/xpto.log"
# All .log files:      -e LOG_SOURCE_PATH="/var/log/**/*.log"
# Specific app:        -e LOG_SOURCE_PATH="/var/log/myapp/*.log"
# Nginx logs:          -e LOG_SOURCE_PATH="/var/log/nginx/*.log"
# Custom path:         -e LOG_SOURCE_PATH="/path/to/your/app.log"

# Check status
docker logs devskin-log-agent

# Optional: Customize with more environment variables
# -e DEVSKIN_APP_NAME="my-application"
# -e DEVSKIN_ENVIRONMENT="production"
# -e LOG_SOURCE_NAME="app-logs"
# -e DEVSKIN_BATCH_SIZE=100
# -e DEVSKIN_FLUSH_INTERVAL=5000`;

      case 'systemd':
        return `# Prerequisites: Node.js 18+ and npm 8+
node --version  # Should be >= 18.0.0
npm --version   # Should be >= 8.0.0

# If needed, install Node.js 18 LTS:
# curl -fsSL https://deb.nodesource.com/setup_18.x | sudo -E bash -
# sudo apt-get install -y nodejs

# Clone the repository
git clone https://github.com/devskin1/devskin-monitoring-log-agent.git
cd devskin-monitoring-log-agent

# Install dependencies and build
npm install
npm run build

# Create configuration file
sudo mkdir -p /etc/devskin
sudo cat > /etc/devskin/log-agent-config.json <<EOF
{
  "serverUrl": "${baseUrl}",
  "apiKey": "${apiKey}",
  "applicationId": "${selectedApplication?.id}",
  "applicationName": "my-application",
  "environment": "production",
  "batchSize": 100,
  "flushInterval": 5000,
  "sources": [
    {
      "name": "app-logs",
      "path": "/var/log/**/*.log",
      "format": "auto",
      "labels": {
        "component": "backend"
      }
    }
  ]
}
EOF

# ⚠️  IMPORTANT: Edit /etc/devskin/log-agent-config.json and replace "path" with your actual log location!
# Path examples you can use:
# Single file:        "path": "/var/log/xpto.log"
# Wildcard - all:     "path": "/var/log/**/*.log"
# Specific app:       "path": "/var/log/myapp/*.log"
# Multiple sources:   Add more objects in "sources" array

# Example with multiple sources:
# sudo cat > /etc/devskin/log-agent-config.json <<EOF
# {
#   "serverUrl": "${baseUrl}",
#   "apiKey": "${apiKey}",
#   "applicationId": "${selectedApplication?.id}",
#   "sources": [
#     {
#       "name": "nginx-access",
#       "path": "/var/log/nginx/access.log",
#       "format": "auto"
#     },
#     {
#       "name": "nginx-error",
#       "path": "/var/log/nginx/error.log",
#       "format": "auto"
#     },
#     {
#       "name": "app-logs",
#       "path": "/var/log/myapp/**/*.log",
#       "format": "json"
#     }
#   ]
# }
# EOF

# Create systemd service
sudo cat > /etc/systemd/system/devskin-log-agent.service <<EOF
[Unit]
Description=DevSkin Log Collection Agent
After=network.target

[Service]
Type=simple
User=root
WorkingDirectory=$(pwd)
ExecStart=$(which node) $(pwd)/dist/index.js --config /etc/devskin/log-agent-config.json
Restart=always
RestartSec=10

[Install]
WantedBy=multi-user.target
EOF

# Start the agent
sudo systemctl daemon-reload
sudo systemctl enable devskin-log-agent
sudo systemctl start devskin-log-agent

# Check status
sudo systemctl status devskin-log-agent`;

      case 'kubernetes':
        return `# Prerequisites: kubectl only!
kubectl version --client

# Create namespace
kubectl create namespace devskin-monitoring

# Create Secret with API key
kubectl create secret generic devskin-log-agent \\
  --from-literal=api-key="${apiKey}" \\
  --from-literal=application-id="${selectedApplication?.id}" \\
  --from-literal=server-url="${baseUrl}" \\
  -n devskin-monitoring

# Deploy as DaemonSet (simple!)
cat <<EOF | kubectl apply -f -
apiVersion: apps/v1
kind: DaemonSet
metadata:
  name: devskin-log-agent
  namespace: devskin-monitoring
spec:
  selector:
    matchLabels:
      app: devskin-log-agent
  template:
    metadata:
      labels:
        app: devskin-log-agent
    spec:
      containers:
      - name: log-agent
        image: devskin/log-agent:latest
        env:
        - name: DEVSKIN_API_KEY
          valueFrom:
            secretKeyRef:
              name: devskin-log-agent
              key: api-key
        - name: DEVSKIN_APPLICATION_ID
          valueFrom:
            secretKeyRef:
              name: devskin-log-agent
              key: application-id
        - name: DEVSKIN_SERVER_URL
          valueFrom:
            secretKeyRef:
              name: devskin-log-agent
              key: server-url
        - name: LOG_SOURCE_PATH
          value: "/var/log/pods/**/*.log"
        - name: LOG_SOURCE_FORMAT
          value: "auto"
        volumeMounts:
        - name: varlog
          mountPath: /var/log
          readOnly: true
      volumes:
      - name: varlog
        hostPath:
          path: /var/log
EOF

# Check status
kubectl get pods -n devskin-monitoring -l app=devskin-log-agent

# ⚠️  IMPORTANT: Edit the LOG_SOURCE_PATH value above with your actual log location!
# Path examples:
# Single file:            "/var/log/xpto.log"
# All pod logs:           "/var/log/pods/**/*.log"
# Specific namespace:     "/var/log/pods/my-namespace*/**/*.log"
# Container logs:         "/var/log/containers/*.log"
# Custom path:            "/path/to/your/app.log"
# Multiple patterns:      Can deploy multiple DaemonSets with different paths`;

      default:
        return '';
    }
  };

  // Show elegant empty state when All Applications is selected
  if (isAllAppsSelected) {
    return (
      <MainLayout>
        <ApplicationRequired
          title={t('install.validation.title')}
          message={t('install.validation.description')}
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('logs.title')}</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              {t('logs.subtitle')}
            </p>
          </div>
          <button
            onClick={() => navigate('/logs')}
            className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            {t('install.back')}
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center space-x-4">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                currentStep >= step
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400'
              }`}>
                {currentStep > step ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <span className="text-sm font-semibold">{step}</span>
                )}
              </div>
              {step < 4 && (
                <div className={`w-16 h-0.5 ${currentStep > step ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-700'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Get API Key */}
        {currentStep === 1 && (
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {t('install.steps.selectApiKey')}
            </h2>

            {/* Information: Why Application Selection is Required */}
            <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
                    {t('install.validation.whyRequired')}
                  </p>
                  <ul className="space-y-1 text-xs text-blue-800 dark:text-blue-400">
                    <li>• {t('install.validation.reason1')}</li>
                    <li>• {t('install.validation.reason2')}</li>
                    <li>• {t('install.validation.reason3')}</li>
                  </ul>
                </div>
              </div>
            </div>

            {loadingApps ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : appsToDisplay.length > 0 ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('install.apiKey.selectExistingDesc', { count: appsToDisplay.length })}
                </p>

                <div className="space-y-2">
                  {appsToDisplay.map((app) => (
                    <div
                      key={app.id}
                      className="flex items-center justify-between p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-indigo-500 dark:hover:border-indigo-500 hover:shadow-md cursor-pointer transition-all"
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 dark:text-white">{app.name}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                          <span>{t('install.apiKey.language')}: {app.language || 'N/A'}</span>
                          <span>•</span>
                          <span>{t('install.apiKey.environment')}: {app.environment}</span>
                          <span>•</span>
                          <span>{t('install.apiKey.created')}: {new Date(app.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <code className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                            {app.api_key.substring(0, 20)}...
                          </code>
                        </div>
                      </div>
                      <button
                        onClick={() => selectExistingApplication(app)}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                      >
                        {t('install.apiKey.useSelected')}
                      </button>
                    </div>
                  ))}
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white dark:bg-gray-900 text-gray-500">{t('install.apiKey.orGenerateNew')}</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={generateApiKey}
                    disabled={generatingKey}
                    className="flex-1 px-4 py-3 text-sm font-medium text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generatingKey ? t('install.apiKey.generating') : t('install.apiKey.generateNew')}
                  </button>
                  <button
                    onClick={() => navigate('/logs')}
                    className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    {t('install.back')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('install.apiKey.selectExistingDesc', { count: 0 })}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={generateApiKey}
                    disabled={generatingKey}
                    className="flex-1 px-6 py-3 text-base font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generatingKey ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('install.apiKey.generating')}
                      </span>
                    ) : (
                      t('install.apiKey.generateNew')
                    )}
                  </button>
                  <button
                    onClick={() => navigate('/logs')}
                    className="px-6 py-3 text-base font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    {t('install.back')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Install Agent */}
        {currentStep === 2 && (
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {t('install.steps.installAgent')}
            </h2>

            {/* API Key Display */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('install.apiKey.title')}:</span>
                <button
                  onClick={() => copyToClipboard(apiKey, 'apiKey')}
                  className={`flex items-center px-3 py-1.5 text-sm rounded-md transition-all ${
                    copiedField === 'apiKey'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/20'
                  }`}
                >
                  {copiedField === 'apiKey' ? (
                    <>
                      <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('install.copy.copied')}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      {t('install.copy.copy')}
                    </>
                  )}
                </button>
              </div>
              <code className="text-sm text-gray-900 dark:text-white break-all font-mono">{apiKey}</code>
            </div>

            {/* Deployment Method Selection */}
            <div className="mb-6">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 block">
                {t('install.steps.chooseMethod')}:
              </label>
              <div className="grid grid-cols-3 gap-3">
                {(['docker', 'systemd', 'kubernetes'] as const).map((method) => (
                  <button
                    key={method}
                    onClick={() => setSelectedMethod(method)}
                    className={`px-4 py-3 text-sm font-medium rounded-lg border-2 transition-all ${
                      selectedMethod === method
                        ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    {method === 'docker' && t('logs.deployment.docker')}
                    {method === 'systemd' && t('logs.deployment.systemd')}
                    {method === 'kubernetes' && t('logs.deployment.kubernetes')}
                  </button>
                ))}
              </div>
            </div>

            {/* Installation Commands */}
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('apm.installation.title')}:
                </span>
                <button
                  onClick={() => copyToClipboard(getInstallCommand(), 'command')}
                  className={`flex items-center px-3 py-1.5 text-sm rounded-md transition-all ${
                    copiedField === 'command'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/20'
                  }`}
                >
                  {copiedField === 'command' ? (
                    <>
                      <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('install.copy.copied')}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      {t('install.copy.command')}
                    </>
                  )}
                </button>
              </div>
              <pre className="bg-gray-900 dark:bg-black text-green-400 text-xs p-4 rounded-lg overflow-x-auto">
                <code>{getInstallCommand()}</code>
              </pre>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setCurrentStep(3)}
                className="flex-1 px-6 py-3 text-base font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-all"
              >
                {t('install.continue')}
              </button>
              <button
                onClick={() => setCurrentStep(1)}
                className="px-6 py-3 text-base font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
              >
                {t('install.back')}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Verify Connection */}
        {currentStep === 3 && (
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {t('install.steps.step')} 3: {t('install.verification.title')}
            </h2>

            {/* Verification Card */}
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-lg p-6 border-2 border-indigo-200 dark:border-indigo-800 mb-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    verification.checking
                      ? 'bg-indigo-100 dark:bg-indigo-900/40'
                      : verification.lastCheck && verification.success
                      ? 'bg-green-100 dark:bg-green-900/40'
                      : verification.lastCheck && !verification.success
                      ? 'bg-yellow-100 dark:bg-yellow-900/40'
                      : 'bg-gray-100 dark:bg-gray-800'
                  }`}>
                    {verification.checking ? (
                      <svg className="animate-spin h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : verification.lastCheck && verification.success ? (
                      <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : verification.lastCheck && !verification.success ? (
                      <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {verification.checking
                      ? t('install.verification.checking')
                      : verification.lastCheck && verification.success
                      ? t('install.verification.success')
                      : verification.lastCheck && !verification.success
                      ? t('install.verification.notFound')
                      : t('install.verification.title')
                    }
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {verification.checking
                      ? t('install.verification.checking')
                      : verification.lastCheck
                      ? verification.message
                      : t('install.verification.noData')
                    }
                  </p>
                  {verification.lastCheck && (
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      {t('install.verification.lastCheck', { time: verification.lastCheck.toLocaleTimeString() })}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={verifyConnection}
                disabled={verification.checking}
                className="flex-1 px-6 py-3 text-base font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {verification.checking ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('install.verification.verifying')}
                  </span>
                ) : verification.lastCheck && !verification.success ? (
                  t('install.verification.button')
                ) : (
                  t('install.verification.button')
                )}
              </button>

              {!verification.success && (
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-6 py-3 text-base font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                >
                  {t('install.back')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Success */}
        {currentStep === 4 && (
          <div className="rounded-xl border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20 shadow-sm p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="ml-3 text-xl font-semibold text-green-900 dark:text-green-100">
                {t('install.complete.title')}
              </h2>
            </div>

            <p className="text-sm text-green-800 dark:text-green-200 mb-6">
              {t('logs.complete.message')}
            </p>

            <div className="flex space-x-4">
              <button
                onClick={() => navigate('/logs')}
                className="flex-1 px-6 py-3 text-base font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
              >
                {t('logs.complete.viewLogs')}
              </button>
              <button
                onClick={() => {
                  setCurrentStep(1);
                  setApiKey('');
                  setSelectedApplication(null);
                  setVerification({ checking: false, success: false, message: t('install.verification.noData') });
                }}
                className="flex-1 px-6 py-3 text-base font-medium text-green-700 dark:text-green-300 bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 rounded-lg hover:bg-green-50 dark:hover:bg-gray-700"
              >
                {t('install.complete.backToAgents')}
              </button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
